import React, { useEffect } from 'react';

import FormEntry from 'controls/react/form/formEntry';
import { AntInput } from 'controls/react/ant/antInput';
import { linkModel, MobxManager, ModelValidator, ValidatableModel } from 'framework/mobx-integration';
import { makeAutoObservable } from 'mobx';
import { Section } from 'controls/react/layout/section';
import { observer } from 'mobx-react';

import { sharedLocalization } from 'vendor-init/tinymce/plugins/localization';
import { MultipleMetricsSelector } from 'controls/react/metricsSelector';
import { DataListEntry } from 'framework/entities/dataList';
import { AntSelect } from 'controls/react/ant/antSelect';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import { PeriodEditor } from './periodEditor';
import { ApplicationState } from 'framework/applicationState';
import { AccountSelector } from 'controls/react/accountSelector';
import { AttributesConfig, CustomAttributes } from './attributes';
import { DataSourceSelectorPlain } from 'controls/designer/features/widgets/datasources/shared/components/data-source-selector/dataSourceSelector';
import { translator } from 'core/localization';

const i = translator(sharedLocalization);

export enum DataSourceType {
	Metric = 'metric',
	Datasource = 'datasource',
}

export class ChartConfig implements ValidatableModel<ChartConfig>, AttributesConfig {
	title: string;
	accountId: string;
	selector: string = 'period';
	aggregateLevel: string = 'auto';
	unit: string = '';
	series: { id: string }[] = [];
	showLabel: string = '';
	showLegend: boolean;
	chartType: string = 'LINE';
	min: string = '';
	max: string = '';
	source: string = DataSourceType.Metric;
	attributes: CustomAttributes = new CustomAttributes();

	get seriesWrapper() {
		return this.series.map((x) => x.id);
	}

	set seriesWrapper(value: string[] | string) {
		if (Array.isArray(value)) {
			this.series = value.map((x) => ({
				id: x,
				name: '',
			}));
		} else {
			this.series = [
				{
					id: value,
				},
			];
		}
	}

	validator = new ModelValidator(this);
	mobx = new MobxManager();

	constructor() {
		this.accountId = ApplicationState.accountId;
		makeAutoObservable(this);

		this.validator.required('series');

		this.mobx.reaction(
			(x) => this.accountId,
			() => {
				this.series = [];
			},
		);
	}

	destroy = () => {
		this.validator.destroy();
		this.mobx.destroy();
	};
}

let chartTypes: DataListEntry<string>[] = null;
const getChartTypes = () => {
	if (chartTypes == null) {
		chartTypes = [
			{ id: 'LINE', name: 'Line' },
			{ id: 'LINE_3D', name: 'Line 3D' },
			{ id: 'BAR', name: 'Bar' },
			{ id: 'BAR_3D', name: 'Bar 3D' },
			{ id: 'BAR_STACKED', name: 'Bar Stacked' },
			{ id: 'BAR_STACKED_3d', name: 'Bar Stacked 3D' },
			{ id: 'PIE', name: 'Pie' },
			{ id: 'PIE_3D', name: 'Pie 3D' },
			{ id: 'AREA', name: 'Area' },
			{ id: 'AREA_STACKED', name: 'Area Stacked' },
			{ id: 'RING', name: 'Ring' },
		];
	}
	return chartTypes;
};

let aggregateLevels: DataListEntry<string>[] = null;
const getAggregateLevels = () => {
	if (aggregateLevels == null) {
		aggregateLevels = [
			{ id: 'auto', name: 'Automatic' },
			{ id: 'hour', name: 'Hour' },
			{ id: 'day', name: 'Day' },
			{ id: 'week', name: 'Week' },
			{ id: 'month', name: 'Month' },
		];
	}
	return aggregateLevels;
};

const ChartForm = observer((props: { config: ChartConfig }) => {
	return (
		<Section childrenPadding={true} appearance={'none'}>
			<FormEntry label={i('Title')}>
				<AntInput {...linkModel(props.config, 'title')} />
			</FormEntry>
			{props.config.source == DataSourceType.Metric && (
				<FormEntry label={i('Source')} model={props.config} modelField={'seriesWrapper'}>
					<MultipleMetricsSelector />
				</FormEntry>
			)}
			{props.config.source == DataSourceType.Datasource && (
				<>
					<FormEntry label={i('Account')} model={props.config} modelField={'accountId'}>
						<AccountSelector />
					</FormEntry>
					<FormEntry label={i('Source')} model={props.config} modelField={'seriesWrapper'}>
						<DataSourceSelectorPlain accountId={props.config.accountId} />
					</FormEntry>
				</>
			)}
			<FormEntry label={i('Aggregate level')}>
				<AntSelect dataList={getAggregateLevels()} {...linkModel(props.config, 'aggregateLevel')} />
			</FormEntry>
			<PeriodEditor config={props.config} />
			<FormEntry label={i('Unit')}>
				<AntInput {...linkModel(props.config, 'unit')} />
			</FormEntry>
			<FormEntry label={i('Show label')}>
				<AntCheckbox {...linkModel(props.config, 'showLabel')} />
			</FormEntry>
			<FormEntry label={i('Show legend')}>
				<AntCheckbox {...linkModel(props.config, 'showLegend')} />
			</FormEntry>
			<FormEntry label={i('Chart type')}>
				<AntSelect dataList={getChartTypes()} {...linkModel(props.config, 'chartType')} />
			</FormEntry>
			<FormEntry label={i('Min')}>
				<AntInput {...linkModel(props.config, 'min')} />
			</FormEntry>
			<FormEntry label={i('Max')}>
				<AntInput {...linkModel(props.config, 'max')} />
			</FormEntry>
		</Section>
	);
});

const MetricChartForm = observer((props: { config: ChartConfig }) => {
	useEffect(() => {
		props.config.source = DataSourceType.Metric;
	}, []);
	return <ChartForm config={props.config} />;
});

export const metricChartComponentDescription = {
	id: 'ceeview/chart',
	type: 'ceeview/chart',
	title: i('Metric chart'),
	config: ChartConfig,
	form: MetricChartForm,
	preview: require('./metricChart.png'),
	defaultSize: {
		width: '300px',
		height: '150px',
	},
};

const DatasourceChartForm = observer((props: { config: ChartConfig }) => {
	useEffect(() => {
		props.config.source = DataSourceType.Datasource;
	}, []);
	return <ChartForm config={props.config} />;
});

export const datasourceChartComponentDescription = {
	id: 'ceeview/datasource',
	type: 'ceeview/chart',
	title: i('Datasource chart'),
	config: ChartConfig,
	form: DatasourceChartForm,
	preview: require('./datasourceBarChart.png'),
	defaultSize: {
		width: '300px',
		height: '150px',
	},
};
